<template>
    <div id="segments-page">
        <div class="container-fluid">
            <confirm
                id="delete-modal"
                ref="confirmDelete"
                @confirm="deleteSegment"
            >
                <template slot="title">
                    {{ t("segments-list-delete_title") }}
                </template>
                <template slot="body">
                    {{ t("segments-list-delete_body") }}
                </template>
            </confirm>

            <div class="d-flex justify-content-between align-items-center flex-wrap gap-md mb-3">
                <h1 class="mb-0">{{ t("segments-list-title") }}</h1>
                <button
                    class="btn btn-primary segments-add-btn"
                    @click="$router.push('/segments/add')"
                    :disabled="maxReached"
                    :aria-label="t('segments-list-add_aria')"
                >
                    <Icon icon-class="ui-plus" size="5" />
                    <span>{{ t("segments-list-add_button") }}</span>
                </button>
            </div>

            <div
                class="alert alert-warning mb-3"
                v-if="maxReached"
            >
                {{ t("segments-list-max_reached_prefix") }}
                {{ $module("segments").max }}
                {{ t("segments-list-max_reached_suffix") }}
            </div>

            <div class="segments-filters mb-3">
                <KvFilter
                    :filters="extraFilters"
                    :default-selected="['search']"
                    :has-period-filter="false"
                    :has-branch-filter="false"
                    :show-labels="true"
                    @changed="updateFilters"
                />
            </div>

            <grid
                ref="segmentsGrid"
                source="segments"
                name="segmentsGrid"
                :quantity-per-page="20"
                :show-search="false"
                :filters="filters"
            >
                <template v-slot:rows="grid">
                    <thead>
                        <tr>
                            <grid-th :grid="grid" :sortable="true" attr="name">
                                {{ t("segments-list-columns_name") }}
                            </grid-th>
                            <grid-th :grid="grid" :sortable="true" attr="created_at" class="col-center segments-created-at-col">
                                {{ t("segments-list-columns_created_at") }}
                            </grid-th>
                            <grid-th :grid="grid" :sortable="true" attr="totalClients" class="col-center">
                                {{ t("segments-list-columns_clients") }}
                            </grid-th>
                            <grid-th :grid="grid" :sortable="true" attr="joined" class="col-center">
                                {{ t("segments-list-columns_movements") }}
                            </grid-th>
                            <grid-th :grid="grid" class="col-center segments-actions-col">
                                {{ t("segments-list-columns_actions") }}
                            </grid-th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="segment in grid.data" :key="segment.id">
                            <td class="font-semibold">
                                <div class="segment-name-cell">
                                    <span class="segment-type-icon">
                                        <KvTooltip
                                            :text="getSegmentTypeTooltip(segment.type)"
                                            placement="right"
                                            :delay="400"
                                        >
                                            <Icon
                                                :icon-class="getSegmentTypeIcon(segment.type)"
                                                size="3"
                                            />
                                        </KvTooltip>
                                    </span>
                                    <a
                                        href="#"
                                        class="segment-name-link"
                                        @click.prevent="goToSegmentClients(segment.id)"
                                        :title="segment.name"
                                    >
                                        {{ segment.name }}
                                    </a>
                                    <span
                                        v-if="segment.error_reason"
                                        class="segment-error-icon"
                                    >
                                        <KvTooltip
                                            :text="getSegmentErrorTooltip(segment.error_reason, false)"
                                            placement="right"
                                            :delay="400"
                                        >
                                            <Icon
                                                icon-class="ui-alert-triangle"
                                                size="3"
                                            />
                                        </KvTooltip>
                                    </span>
                                </div>
                            </td>
                            <td class="col-center segments-created-at-col">
                                <human-date
                                    v-if="segment.created_at"
                                    :date="segment.created_at"
                                    :format="t('dates-format_date')"
                                />
                            </td>
                            <td class="col-center">
                                <a
                                    href="#"
                                    class="segment-clients-link"
                                    @click.prevent="goToSegmentClients(segment.id)"
                                >
                                    {{ segment.totalClients }}
                                </a>
                            </td>
                            <td class="col-center">
                                <KvTooltip
                                    :text="t('segments-list-last_30_days')"
                                    placement="bottom"
                                    :delay="400"
                                >
                                    <span class="movement-pill">
                                        <span class="movement joined">+{{ segment.last30days.joined }}</span>
                                        <span class="movement-separator">/</span>
                                        <span class="movement left">-{{ segment.last30days.left }}</span>
                                    </span>
                                </KvTooltip>
                            </td>
                            <td class="col-center">
                                <div class="segment-row-actions visible-on-hover">
                                    <KvPopup
                                        v-if="segment.type === 'manual'"
                                        placement="bottom right"
                                        min-width="180px"
                                    >
                                        <template #trigger="{ toggle }">
                                            <button
                                                type="button"
                                                class="action-btn"
                                                @click="toggle()"
                                                :aria-label="t('segments-list-manage_members_aria', [segment.name])"
                                            >
                                                <Icon
                                                    icon-class="ui-users-01"
                                                    size="5"
                                                    :tooltip="t('segments-list-manage_members')"
                                                />
                                            </button>
                                        </template>
                                        <template #content="{ toggle }">
                                            <div class="members-popup">
                                                <button
                                                    type="button"
                                                    class="members-popup__option"
                                                    @click="goToMembers(segment.id, 'add', toggle)"
                                                >
                                                    {{ t("segments-list-add_members") }}
                                                </button>
                                                <button
                                                    type="button"
                                                    class="members-popup__option"
                                                    @click="goToMembers(segment.id, 'remove', toggle)"
                                                >
                                                    {{ t("segments-list-remove_members") }}
                                                </button>
                                            </div>
                                        </template>
                                    </KvPopup>
                                    <router-link
                                        :to="'/segments/edit/' + segment.id"
                                        class="action-btn"
                                        :aria-label="t('segments-list-edit_segment_aria', [segment.name])"
                                    >
                                        <Icon icon-class="ui-edit-05" size="5" :tooltip="t('segments-list-edit')" />
                                    </router-link>
                                    <button
                                        type="button"
                                        class="action-btn action-btn--danger"
                                        @click="confirmDelete(segment.id)"
                                        :aria-label="t('segments-list-delete_segment_aria', [segment.name])"
                                    >
                                        <Icon icon-class="ui-trash-01" size="5" :tooltip="t('segments-list-delete')" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </template>

                <template v-slot:list="grid">
                    <div class="segment-mobile-item" v-for="segment in grid.data" :key="'mobile-' + segment.id">
                        <div class="segment-mobile-item__top">
                            <span
                                class="segment-type-tag"
                                :class="'segment-type-tag--' + segment.type"
                            >
                                {{ getSegmentTypeTooltip(segment.type) }}
                            </span>
                            <div class="segment-mobile-item__actions">
                                <KvPopup
                                    v-if="segment.type === 'manual'"
                                    placement="bottom right"
                                    min-width="180px"
                                >
                                    <template #trigger="{ toggle }">
                                        <button
                                            type="button"
                                            class="action-btn"
                                            @click="toggle()"
                                            :aria-label="t('segments-list-manage_members_aria', [segment.name])"
                                        >
                                            <Icon icon-class="ui-users-01" size="5" :tooltip="t('segments-list-manage_members')" />
                                        </button>
                                    </template>
                                    <template #content="{ toggle }">
                                        <div class="members-popup">
                                            <button
                                                type="button"
                                                class="members-popup__option"
                                                @click="goToMembers(segment.id, 'add', toggle)"
                                            >
                                                {{ t("segments-list-add_members") }}
                                            </button>
                                            <button
                                                type="button"
                                                class="members-popup__option"
                                                @click="goToMembers(segment.id, 'remove', toggle)"
                                            >
                                                {{ t("segments-list-remove_members") }}
                                            </button>
                                        </div>
                                    </template>
                                </KvPopup>
                                <router-link
                                    :to="'/segments/edit/' + segment.id"
                                    class="action-btn"
                                    :aria-label="t('segments-list-edit_segment_aria', [segment.name])"
                                >
                                    <Icon icon-class="ui-edit-05" size="5" :tooltip="t('segments-list-edit')" />
                                </router-link>
                                <button
                                    type="button"
                                    class="action-btn action-btn--danger"
                                    @click="confirmDelete(segment.id)"
                                    :aria-label="t('segments-list-delete_segment_aria', [segment.name])"
                                >
                                    <Icon icon-class="ui-trash-01" size="5" :tooltip="t('segments-list-delete')" />
                                </button>
                            </div>
                        </div>

                        <a
                            href="#"
                            class="segment-name-link segment-name-link--mobile"
                            @click.prevent="goToSegmentClients(segment.id)"
                            :title="segment.name"
                        >
                            {{ segment.name }}
                        </a>

                        <div
                            v-if="segment.error_reason"
                            class="segment-error-inline"
                        >
                            <Icon icon-class="ui-alert-triangle" size="2" />
                            {{ getSegmentErrorTooltip(segment.error_reason, true) }}
                        </div>

                        <div class="segment-mobile-item__meta">
                            <div class="segment-mobile-item__meta-item">
                                <span class="segment-mobile-item__meta-label">
                                    {{ t("segments-list-columns_created_at") }}
                                </span>
                                <span class="segment-mobile-item__meta-value">
                                    <human-date
                                        v-if="segment.created_at"
                                        :date="segment.created_at"
                                        :format="t('dates-format_date')"
                                    />
                                    <span v-else>—</span>
                                </span>
                            </div>
                            <div class="segment-mobile-item__meta-item">
                                <span class="segment-mobile-item__meta-label">
                                    {{ t("segments-list-columns_clients") }}
                                </span>
                                <a
                                    href="#"
                                    class="segment-mobile-item__meta-value segment-clients-link"
                                    @click.prevent="goToSegmentClients(segment.id)"
                                >
                                    {{ segment.totalClients }}
                                </a>
                            </div>
                            <div class="segment-mobile-item__meta-item">
                                <span class="segment-mobile-item__meta-label">
                                    {{ t("segments-list-columns_movements") }}
                                </span>
                                <KvTooltip
                                    :text="t('segments-list-last_30_days')"
                                    placement="bottom"
                                    :delay="400"
                                >
                                    <span class="segment-mobile-item__meta-value movement-pill">
                                        <span class="movement joined">+{{ segment.last30days.joined }}</span>
                                        <span class="movement-separator">/</span>
                                        <span class="movement left">-{{ segment.last30days.left }}</span>
                                    </span>
                                </KvTooltip>
                            </div>
                        </div>
                    </div>
                </template>
            </grid>
        </div>
    </div>
</template>

<script>
import keviApi from "../keviApi";
import Icon from "../components/icon/icon";
import KvTooltip from "../components/ui/kv-tooltip.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvFilter from "../components/reports/kv-filter-bar.vue";
import HumanDate from "../components/human-date/human-date";
import grid from "../components/grid/grid.vue";

export default {
    components: { Icon, KvTooltip, KvPopup, KvFilter, HumanDate, grid },
    data: () => ({
        segmentToDelete: null,
        totalSegments: 0,
        filters: {},
    }),
    computed: {
        maxReached() {
            return this.$module("segments", (o) => o.max && o.max <= this.totalSegments);
        },
        extraFilters() {
            return [
                {
                    component: "textfield",
                    label: this.t("segments-list-search_placeholder"),
                    name: "search",
                    removable: false,
                },
                {
                    component: "kv-select",
                    label: this.t("segments-list-filters_type"),
                    name: "type",
                    removable: true,
                    options: {
                        options: [
                            { value: "manual", label: this.t("segments-list-manual_badge") },
                            { value: "dynamic", label: this.t("segments-list-dynamic_badge") },
                        ],
                    },
                },
                {
                    component: "period",
                    label: this.t("segments-list-filters_created_period"),
                    name: "createdPeriod",
                    removable: true,
                },
            ];
        },
    },
    mounted() {
        this.fetchSegmentsCount();
        this.$nextTick(() => {
            const gridRef = this.$refs.segmentsGrid;
            if (!gridRef || this.$route.query.segmentsGrid) {
                return;
            }
            gridRef.sortingBy = { attr: "created_at", method: "desc" };
            gridRef.update();
        });
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        updateFilters(filters) {
            this.filters = filters;
        },
        fetchSegmentsCount() {
            this.$http(keviApi.getOptions("segments", "GET")).then((response) => {
                if (Array.isArray(response.body)) {
                    this.totalSegments = response.body.length;
                }
            });
        },
        goToMembers(segmentId, operation, togglePopup) {
            togglePopup(false);
            this.$router.push(`/segments/${segmentId}/members/${operation}`);
        },
        confirmDelete(id) {
            this.$refs.confirmDelete.open();
            this.segmentToDelete = id;
        },
        deleteSegment() {
            const data = { id: this.segmentToDelete };
            const options = keviApi.getOptions("segments/delete", "POST", data);
            this.$http(options).then(() => {
                this.$refs.segmentsGrid.update();
                this.fetchSegmentsCount();
                this.$store.dispatch(
                    "notification/success",
                    this.t("segments-list-deleted_success")
                );
            }).catch((error) => {
                const body = (error && error.body) || {};
                const text = body.message === "segment-in-use-by-tier"
                    ? this.t("segments-list-delete_in_use_by_tier", { name: (body.params && body.params.tierName) || "" })
                    : this.t("segments-list-delete_error");
                this.$store.dispatch("notification/error", { text });
            });
        },
        goToSegmentClients(segmentId) {
            const filter = JSON.stringify({ "client-segments": [segmentId] });
            this.$router.push({
                path: "/clients",
                query: { filter },
            });
        },
        getSegmentTypeIcon(type) {
            return type === "manual" ? "ui-list" : "ui-clock";
        },
        getSegmentTypeTooltip(type) {
            return type === "manual"
                ? this.t("segments-list-manual_badge")
                : this.t("segments-list-dynamic_badge");
        },
        getSegmentErrorTooltip(errorReason, isShort = false) {
            if (!errorReason) {
                return null;
            }

            const prefix = isShort ? "segments-error-short-" : "segments-error-";
            const genericKey = isShort ? "segments-error-short-generic" : "segments-error-generic";

            return this.t(
                `${prefix}${errorReason}`,
                null,
                this.t(genericKey),
            );
        },
    },
};
</script>

<style scoped lang="scss">
h1 {
    font-size: 24px;
    color: #1f2a37;
}

.segments-add-btn {
    display: flex;
    gap: 4px;
    align-items: center;
    justify-content: center;
}

.segment-name-cell {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.segment-type-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
}

.segment-error-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    color: #b54708;
}

.segment-name-link,
.segment-clients-link {
    color: #374151;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
}

.segment-name-link {
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    min-width: 0;
    max-width: fit-content;
}

.segment-name-link--mobile {
    width: 100%;
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    word-break: break-word;
    margin-bottom: 0;
}

.segment-type-tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 999px;
    line-height: 1.3;
    letter-spacing: 0.02em;

    &--manual {
        background: #f3f4f6;
        color: #374151;
    }

    &--dynamic {
        background: #eff6ff;
        color: #1d4ed8;
    }
}

.movement-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;

    .movement.joined {
        color: #16A34A;
    }

    .movement.left {
        color: #DC2626;
    }

    .movement-separator {
        color: #D1D5DB;
        font-weight: 500;
    }
}

.segment-row-actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border: none;
    color: #374151;
    padding: 4px;
    cursor: pointer;

    &--danger {
        color: #DC2626;
    }
}

.members-popup {
    display: flex;
    flex-direction: column;
    padding: 4px 0;

    &__option {
        border: none;
        background: none;
        text-align: left;
        padding: 8px 16px;
        font-size: 14px;
        color: #172a37;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            background: #f3f4f6;
        }
    }
}

.segment-mobile-item {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 16px;
    margin-bottom: 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);

    .segment-error-inline {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        font-size: 12px;
        color: #b54708;
        background: rgba(181, 71, 8, 0.08);
        border-radius: 6px;
        padding: 6px 10px;
        margin-top: 8px;
        line-height: 1.4;
    }

    &__top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
    }

    &__actions {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;

        .action-btn {
            padding: 6px;
            border-radius: 8px;

            &:active {
                background: #f3f4f6;
            }
        }
    }

    &__meta {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        padding-top: 14px;
        margin-top: 14px;
        border-top: 1px solid #f3f4f6;
    }

    &__meta-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    &__meta-label {
        font-size: 11px;
        font-weight: 500;
        color: #9ca3af;
        line-height: 1.2;
    }

    &__meta-value {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
        line-height: 1.3;
        min-width: 0;
    }
}

::v-deep .segments-actions-col {
    width: 140px;
}

::v-deep .segments-created-at-col {
    min-width: 120px;
}
</style>
